import CustomDatePicker from '@/components/shared/CustomDatePicker';
import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, ArrowUpDown, CheckCircle2, Package, Trash2, ExternalLink, Undo2, Pencil, FileText, Truck, ChevronDown, ChevronUp, FileDown, RotateCcw, AlertCircle, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Card, CardContent } from '@/components/ui/card';

import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import LastUpdated from '@/components/ui/LastUpdated';
import { useSession } from '@/lib/SessionContext';
import { canManageSelesai } from '@/lib/AuthSession';
import SuratJalanPrint from '@/components/production/SuratJalanPrint';
import { exportToPDF } from '@/utils/exportPDF';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';

function formatRupiah(val) {
  if (!val && val !== 0) return '-';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}

export default function WorkspaceHistoryClientPO() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('shipped_at');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  React.useEffect(() => { setCurrentPage(1); }, [search, sortBy, dateFrom, dateTo, typeFilter]);
  const [suratJalanOrder, setSuratJalanOrder] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [returnModalData, setReturnModalData] = useState(null);
  const queryClient = useQueryClient();
  const { sapaan, isFadli } = useAsaPersonality();

  const isDirectCompletion = (order) => {
    if (!order) return false;
    if (order.diselesaikan_langsung === true || order.completion_type === 'direct') return true;
    if ((order.status === 'done' || order.is_shipped) && (!order.task_stock_deducted || (!order.task_stock_deducted.racik && !order.task_stock_deducted.filling))) {
      return true;
    }
    return false;
  };

  const { member, companySettings: globalCompanySettings } = useSession();
  const canEdit = canManageSelesai(member);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['shipped-orders'],
    queryFn: () => base44.entities.ProductionOrder.filter({ is_shipped: true }),
  });

  const { data: settingsList = [] } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });
  const localSettings = settingsList[0] || {};
  const companySettings = (localSettings.logo_url || localSettings.stempel_url || localSettings.ttd_url) ? localSettings : { ...globalCompanySettings, ...localSettings };

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.ProductionOrder.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      toast.success('PO dihapus');
    },
  });

  const unshipMutation = useMutation({
    mutationFn: (id) => base44.entities.ProductionOrder.update(id, { status: 'dalam_pengiriman', is_shipped: false, shipped_at: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      toast.success('PO dikembalikan ke status Dalam Pengiriman');
    },
  });

  const returnMutation = useMutation({
    mutationFn: ({ id, stage }) => base44.entities.ProductionOrder.update(id, { 
      status: 'berjalan', 
      is_shipped: false,
      is_returned_po: true,
      return_stage: stage || 'Awal'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      toast.success('PO berhasil di-return ke daftar produksi aktif!');
      setReturnModalData(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ProductionOrder.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      toast.success('Data berhasil diperbarui');
      setEditOrder(null);
    },
  });

  const handleExport = () => {
    const subtitle = (dateFrom || dateTo)
      ? `Periode: ${dateFrom || '...'} s/d ${dateTo || '...'} · ${filtered.length} PO`
      : `Total ${filtered.length} PO telah dikirim`;
    exportToPDF({
      title: 'Laporan Orderan Selesai',
      subtitle,
      filename: 'Orderan-Selesai',
      columns: [
        { key: 'order_number', label: 'No. PO', render: o => `PO-${o.order_number || '-'}` },
        { key: 'brand_name', label: 'Brand' },
        { key: 'product_name', label: 'Produk', render: o => o.product_name || '-' },
        { key: 'final_qty', label: 'Qty', render: o => `${((o.final_qty || o.target_qty || 0).toLocaleString())} pcs` },
        { key: 'ukuran_botol_ml', label: 'Ukuran', render: o => o.ukuran_botol_ml ? `${o.ukuran_botol_ml} ml` : '-' },
        { key: 'shipped_at', label: 'Tgl Kirim', render: o => o.shipped_at ? new Date(o.shipped_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : '-' },
        { key: 'shipping_via', label: 'Via', render: o => o.shipping_via || o.courier || '-' },
        { key: 'tracking_number', label: 'No. Resi', render: o => o.tracking_number || '-' },
        { key: 'ongkir_payer', label: 'Ongkir', render: o => o.ongkir_payer === 'pabrik' ? `Pabrik${o.ongkir_amount ? ` (${formatRupiah(o.ongkir_amount)})` : ''}` : o.ongkir_payer === 'customer' ? 'Customer' : '-' },
        { key: 'notes', label: 'Catatan', render: o => o.notes || '-' },
      ],
      rows: filteredForExport,
    });
  };

  const applyFilters = (list) =>
    list.filter(o => {
      const matchSearch = !search ||
        o.brand_name?.toLowerCase().includes(search.toLowerCase()) ||
        o.product_name?.toLowerCase().includes(search.toLowerCase());
      const shippedDateStr = o.shipped_at ? o.shipped_at.substring(0, 10) : '';
      const matchFrom = !dateFrom || (shippedDateStr && shippedDateStr >= dateFrom);
      const matchTo = !dateTo || (shippedDateStr && shippedDateStr <= dateTo);
      const matchType = typeFilter === 'all' 
        ? true 
        : typeFilter === 'direct' 
          ? isDirectCompletion(o) 
          : !isDirectCompletion(o);
    }).sort((a, b) => {
      if (sortBy === 'shipped_at') {
        const getExactTime = (o) => {
          const d1 = o.tanggal_selesai;
          const d2 = o.completed_at;
          const d3 = o.updated_date;
          const d4 = o.shipped_at;
          const d5 = o.created_date;
          if (d1 && String(d1).length > 10) return new Date(d1).getTime();
          if (d2 && String(d2).length > 10) return new Date(d2).getTime();
          if (d3 && String(d3).length > 10) return new Date(d3).getTime();
          if (d4 && String(d4).length > 10) return new Date(d4).getTime();
          if (d5 && String(d5).length > 10) return new Date(d5).getTime();
          if (d4) return new Date(d4).getTime();
          if (d1) return new Date(d1).getTime();
          if (d3) return new Date(d3).getTime();
          return 0;
        };
        return getExactTime(b) - getExactTime(a);
      }
      if (sortBy === 'brand_name') return (a.brand_name || '').localeCompare(b.brand_name || '');
      return 0;
    });

  const filtered = applyFilters(orders);
  const filteredForExport = filtered;

  const getProductItems = (order) => {
    const items = [];
    if (order.products && order.products.length > 0) {
      order.products.forEach(p => {
        items.push({
          nama: p.variant_name || p.product_name || order.brand_name,
          keterangan: p.ukuran_botol_ml ? `${p.ukuran_botol_ml} ml` : '-',
          qty: p.qty_akhir || p.target_qty || p.qty || 0,
        });
      });
    } else {
      items.push({
        nama: order.product_name || order.brand_name,
        keterangan: order.ukuran_botol_ml ? `${order.ukuran_botol_ml} ml` : '-',
        qty: order.final_qty || order.target_qty || 0,
      });
    }
    return items;
  };

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount
  } = useBatchSelection(filtered, o => o.id);

  const handleBulkDelete = async () => {
    if (!confirm(`Hapus ${selectedCount} PO terpilih?`)) return;
    await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
    clearSelection();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Arsip Selesai</h1>
          <p className="text-muted-foreground text-sm mt-1">{orders.length} PO telah selesai dikirim</p>
        </div>
        <Button variant="outline" onClick={handleExport} className="gap-2 shrink-0">
          <FileDown className="w-4 h-4" /> Export PDF
        </Button>
      </div>

      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={filtered.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={handleBulkDelete}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cari brand atau produk..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-52"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Dari:</span>
          <CustomDatePicker value={dateFrom} onChange={setDateFrom} className="w-36 h-9" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground whitespace-nowrap">S/d:</span>
          <CustomDatePicker value={dateTo} onChange={setDateTo} className="w-36 h-9" />
        </div>
        {(dateFrom || dateTo || typeFilter !== 'all') && (
          <button className="text-xs text-muted-foreground hover:text-foreground h-9 px-2" onClick={() => { setDateFrom(''); setDateTo(''); setTypeFilter('all'); }}>Reset</button>
        )}
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48 h-9">
            <SelectValue placeholder="Tipe Selesai" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tipe Selesai</SelectItem>
            <SelectItem value="direct">Diselesaikan Langsung</SelectItem>
            <SelectItem value="standard">Alur Produksi Penuh</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-44 h-9 ml-auto">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="shipped_at">Tanggal Selesai</SelectItem>
            <SelectItem value="brand_name">Nama Brand (A-Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array(4).fill(0).map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Belum ada PO yang selesai dikirim</p>
        </div>
      ) : (
        <>
        <div className="space-y-3">
          {filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(order => {
            const isExpanded = expanded === order.id;
            const items = getProductItems(order);
            const via = order.shipping_via || order.courier;

            const isReturned = order.status === 'returned';
            const isShipping = order.status === 'shipping';
            const cardClass = isReturned 
               ? "border-red-200/40 bg-red-50/20" 
               : isShipping ? "border-blue-200/40 bg-blue-50/20" 
               : "border-green-200/40 bg-green-50/20";
            
            const Icon = isReturned ? AlertCircle : (isShipping ? Truck : CheckCircle2);
            const iconColor = isReturned ? "text-red-600 bg-red-100" : (isShipping ? "text-blue-600 bg-blue-100" : "text-green-600 bg-green-100");

            return (
              <Card 
                key={order.id} 
                className={`${cardClass} group hover:shadow-xl transition-all duration-300 rounded-[2rem] border-white/60 bg-white/80  ${
                  isSelectionMode && selectedIds.has(order.id) ? 'ring-2 ring-rose-500 bg-rose-50/20' : ''
                }`}
                onClick={() => isSelectionMode && toggleSelection(order.id)}
              >
                <CardContent className="p-4">
                  {/* Main row */}
                  <div className="flex items-center gap-4">
                    {isSelectionMode && (
                      <div className="shrink-0 animate-in fade-in slide-in-from-left-2 duration-200" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedIds.has(order.id)} 
                          onCheckedChange={() => toggleSelection(order.id)}
                          className="data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
                        />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${iconColor}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-base">{order.brand_name}</h3>
                        {isDirectCompletion(order) && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-300 shadow-xs">
                            <Zap className="w-2.5 h-2.5 text-amber-700" /> Diselesaikan Langsung
                          </span>
                        )}
                        {order.product_name && (
                          <span className="text-xs text-muted-foreground">— {order.product_name}</span>
                        )}
                        {order.ukuran_botol_ml && (
                          <Badge variant="outline" className="text-xs">{order.ukuran_botol_ml} ml</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {order.shipped_at && (
                          <p className="text-xs font-medium text-green-700">
                            {new Date(order.shipped_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}
                          </p>
                        )}
                        {(order.final_qty > 0 || order.target_qty > 0) && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Package className="w-3 h-3" /> {(order.final_qty || order.target_qty)?.toLocaleString()} pcs
                          </p>
                        )}
                        {via && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Truck className="w-3 h-3" /> {via}{order.tracking_number ? ` · ${order.tracking_number}` : ''}
                          </p>
                        )}
                        {/* Ongkir badge */}
                        {order.ongkir_payer && (
                          <Badge
                            variant="outline"
                            className={`text-xs ${order.ongkir_payer === 'pabrik' ? 'border-orange-300 text-orange-700 bg-orange-50' : 'border-blue-300 text-blue-700 bg-blue-50'}`}
                          >
                            Ongkir: {order.ongkir_payer === 'pabrik' ? `Pabrik${order.ongkir_amount ? ` (${formatRupiah(order.ongkir_amount)})` : ''}` : 'Customer'}
                          </Badge>
                        )}
                        <LastUpdated date={order.updated_date || order.created_date} />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {order.shipments && order.shipments.length > 0 ? (
                        order.shipments.map((shipment, idx) => {
                          const fakedOrder = {
                            ...order,
                            shipping_details: shipment.items,
                            shipping_address: shipment.alamat_pengiriman ? `${shipment.alamat_pengiriman.penerima} - ${shipment.alamat_pengiriman.telepon} - ${shipment.alamat_pengiriman.alamat_lengkap} ${shipment.alamat_pengiriman.kode_pos}` : order.shipping_address,
                            shipping_via: shipment.shipping_via || order.shipping_via,
                            ongkir_payer: shipment.ongkir_payer || order.ongkir_payer,
                            ongkir_amount: shipment.ongkir_amount !== undefined ? shipment.ongkir_amount : order.ongkir_amount,
                            tracking_number: shipment.resi || order.tracking_number,
                            pic: shipment.pic || order.pic,
                          };
                          return (
                            <Button
                              key={idx}
                              variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground relative group"
                              title={`Surat Jalan (Destinasi ${idx + 1})`}
                              onClick={() => setSuratJalanOrder(fakedOrder)}
                            >
                              <FileText className="w-4 h-4" />
                              {order.shipments.length > 1 && (
                                <span className="absolute -bottom-1 -right-1 text-[8px] font-bold bg-muted px-1 rounded-sm">{idx + 1}</span>
                              )}
                            </Button>
                          );
                        })
                      ) : (
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          title="Surat Jalan"
                          onClick={() => setSuratJalanOrder(order)}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <Link to={`/office/production/${order.id}`} title="Edit Detail & Pengiriman PO">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                      <button
                        className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground"
                        onClick={() => setExpanded(isExpanded ? null : order.id)}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {canEdit && !isSelectionMode && (
                        <>

                          {order.status !== 'returned' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setReturnModalData(order)}
                              disabled={returnMutation.isPending}
                              className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2"
                              title="Return Produksi"
                            >
                              <RotateCcw className="w-4 h-4 mr-2" /> Return
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-destructive/60 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Hapus PO"
                            onClick={(e) => { e.stopPropagation(); startSelection(order.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-green-200/60 space-y-4">
                      {/* Product Table */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Detail Produk</p>
                        <div className="rounded-xl border border-border overflow-hidden">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-muted/60">
                                <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Nama Produk</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Ukuran</th>
                                <th className="text-center px-3 py-2 text-xs font-semibold text-muted-foreground">Qty</th>
                                <th className="text-center px-3 py-2 text-xs font-semibold text-muted-foreground">Satuan</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, idx) => (
                                <tr key={idx} className="border-t border-border">
                                  <td className="px-3 py-2 font-medium">{item.nama}</td>
                                  <td className="px-3 py-2 text-muted-foreground">{item.keterangan}</td>
                                  <td className="px-3 py-2 text-center font-bold">{item.qty > 0 ? Math.round(item.qty).toLocaleString('id-ID') : '-'}</td>
                                  <td className="px-3 py-2 text-center text-muted-foreground">pcs</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Ongkir detail */}
                      {order.shipments && order.shipments.length > 0 ? (
                        <div className="space-y-3">
                          {order.shipments.map((shipment, idx) => (
                            <div key={idx} className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                              <p className="text-xs font-bold text-slate-800 mb-2">Destinasi {idx + 1}</p>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                  { label: 'Via', value: shipment.shipping_via || '-' },
                                  { label: 'No. Resi', value: shipment.resi || '-' },
                                  {
                                    label: 'Ongkir',
                                    value: shipment.ongkir_payer === 'pabrik'
                                      ? `Pabrik${shipment.ongkir_amount ? ` — ${formatRupiah(shipment.ongkir_amount)}` : ''}`
                                      : shipment.ongkir_payer === 'customer' ? 'Customer'
                                      : '-'
                                  },
                                  { label: 'Alamat', value: shipment.alamat_pengiriman ? `${shipment.alamat_pengiriman.penerima} - ${shipment.alamat_pengiriman.alamat_lengkap}` : '-' },
                                ].map(({ label, value }, i) => (
                                  <div key={i}>
                                    <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
                                    <p className="text-xs font-semibold truncate" title={value}>{value}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { label: 'Via', value: via || '-' },
                            { label: 'No. Resi', value: order.tracking_number || '-' },
                            {
                              label: 'Ongkir',
                              value: order.ongkir_payer === 'pabrik'
                                ? `Pabrik${order.ongkir_amount ? ` — ${formatRupiah(order.ongkir_amount)}` : ''}`
                                : order.ongkir_payer === 'customer' ? 'Customer'
                                : '-'
                            },
                            { label: 'Alamat', value: order.shipping_address || '-' },
                          ].map(({ label, value }) => (
                            <div key={label} className="bg-muted/30 rounded-xl p-3">
                              <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                              <p className="text-sm font-semibold truncate" title={value}>{value}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Notes */}
                      {order.notes && (
                        <div className="bg-amber-50/60 border border-amber-200/60 rounded-xl px-3 py-2 text-xs text-amber-800">
                          <span className="font-semibold">Catatan: </span>{order.notes}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* ── Pagination Controls ── */}
        {filtered.length > 0 && (
          <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
              <div className="w-[80px]">
                <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 25, 50, 100].map(n => (
                      <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
              >
                &lt;
              </button>
              
              {(() => {
                const totalPages = Math.ceil(filtered.length / itemsPerPage);
                const pages = [];
                for(let i=1; i<=totalPages; i++) {
                  if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                          currentPage === i 
                            ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                            : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  } else if (i === currentPage - 2 || i === currentPage + 2) {
                    pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                  }
                }
                return pages;
              })()}
              
              <button 
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1))}
                disabled={currentPage >= Math.ceil(filtered.length / itemsPerPage)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
              >
                &gt;
              </button>
            </div>
          </div>
        )}
        </>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>



      {/* Surat Jalan */}
      {suratJalanOrder && (
        <SuratJalanPrint
          order={suratJalanOrder}
          companySettings={companySettings}
          onClose={() => setSuratJalanOrder(null)}
        />
      )}

      {/* Modal Return Asa */}
      {returnModalData && (
        <div className="fixed inset-0 z-[1000] bg-black/60  flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-xl border border-[#E6D5B8]">
             <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
               <RotateCcw className="w-8 h-8" />
             </div>
             <h3 className="text-lg font-bold text-[#302621] mb-2">Pesan dari Asa</h3>
             <p className="text-sm text-slate-600 mb-6 text-left leading-relaxed">
               {isFadli
                 ? `Fadli, ini PO untuk ${returnModalData.brand_name || returnModalData.customerName} beneran mau dibalikin ke produksi nih? Asa catet ya, mau dibalikin ke progres yang mana?`
                 : `Halo ${sapaan}, Anda akan mengembalikan PO ini ke produksi aktif. Pilih progres yang sesuai:`
               }
             </p>
             
             <div className="flex flex-col gap-3">
               {['Awal', 'Mixing', 'Filling', 'Pengemasan'].map(stage => (
                 <Button 
                   key={stage}
                   variant="outline" 
                   className="w-full justify-start border-slate-200 hover:border-rose-500 hover:text-rose-600 hover:bg-rose-50"
                   onClick={() => returnMutation.mutate({ id: returnModalData.id, stage })}
                 >
                   Kembalikan ke {stage}
                 </Button>
               ))}
             </div>
             
             <Button variant="ghost" className="w-full mt-4 text-slate-500 hover:bg-slate-100" onClick={() => setReturnModalData(null)}>Batal</Button>
          </div>
        </div>
      )}
    </div>
  );
}
